import { io } from 'socket.io-client';
import { onTokenRefreshed, onLoggedOut } from './tokenEvents';
import { getFreshAccessToken } from './TerserahApi';

class ChatSocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.currentToken = null;
    this.listeners = new Map();
    this.refreshing = false;

    // B6: keep the handshake token in sync with the API client so the
    // socket does not die when the 15-minute access token expires.
    onTokenRefreshed((token) => this.updateToken(token));
    onLoggedOut(() => this.disconnect());
  }

  /** Update the token used for (re)connections without dropping the socket. */
  updateToken(token) {
    if (!token || token === this.currentToken) return;
    this.currentToken = token;
    if (this.socket) {
      this.socket.auth = { token, platform: 'web' };
    }
  }

  connect(token) {
    // Si ya hay un socket conectado con el mismo token, retornarlo
    if (this.socket && this.connected && this.currentToken === token) {
      return this.socket;
    }

    // Si hay un socket existente (conectado o no), limpiarlo completamente
    if (this.socket) {
      try {
        this.socket.removeAllListeners();
        this.socket.disconnect();
        this.socket.close();
      } catch (e) {
        // Ignorar errores al cerrar
      }
      this.socket = null;
      this.connected = false;
    }

    // Guardar el token actual
    this.currentToken = token;

    // Socket.IO must connect to root, not /api endpoint
    let SOCKET_URL = process.env.REACT_APP_API_URL || 'https://api.terserah.id';
    // Remove /api suffix if present
    SOCKET_URL = SOCKET_URL.replace(/\/api$/, '');
    
    console.log('[ChatSocket] Connecting to:', SOCKET_URL);
    
    this.socket = io(SOCKET_URL, {
      // A function is evaluated on every (re)connection attempt, so a token
      // refreshed in the background is picked up automatically.
      auth: (cb) => cb({ token: this.currentToken, platform: 'web' }),
      query: {
        platform: 'web'
      },
      transports: ['polling'],
      upgrade: false,
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
      timeout: 20000,
      autoConnect: true,
      forceNew: true,
      path: '/socket.io/'
    });

    this.socket.on('connect', () => {
      this.connected = true;
      console.log('Socket.IO connected:', this.socket.id);
    });

    this.socket.on('disconnect', (reason) => {
      this.connected = false;
      console.log('Socket.IO disconnected:', reason);
    });

    this.socket.on('connect_error', async (error) => {
      const message = String(error?.message || '').toLowerCase();
      const isAuthError =
        message.includes('auth') ||
        message.includes('token') ||
        message.includes('jwt') ||
        message.includes('unauthor') ||
        message.includes('forbidden');

      if (!isAuthError || this.refreshing) {
        console.error('Socket.IO connection error:', error);
        return;
      }

      // Most likely the access token expired: refresh it and retry once.
      this.refreshing = true;
      try {
        const fresh = await getFreshAccessToken();
        if (fresh) {
          this.updateToken(fresh);
          if (this.socket) this.socket.connect();
        }
      } catch (e) {
        // ignore — reconnection will be attempted again later
      } finally {
        this.refreshing = false;
      }
    });

    return this.socket;
  }

  disconnect() {
    if (this.socket) {
      try {
        this.socket.removeAllListeners();
        this.socket.disconnect();
        this.socket.close();
      } catch (e) {
        // Ignorar errores al cerrar
      }
      this.socket = null;
      this.connected = false;
      this.currentToken = null;
      this.listeners.clear();
    }
  }

  // Send message
  sendMessage(data) {
    if (!this.socket || !this.connected) {
      throw new Error('Socket not connected');
    }
    
    return new Promise((resolve, reject) => {
      this.socket.emit('message:send', data, (response) => {
        if (response.success) {
          resolve(response.message);
        } else {
          reject(new Error(response.error));
        }
      });
    });
  }

  // Join conversation room
  joinConversation(adPublicId) {
    if (this.socket && this.connected) {
      this.socket.emit('conversation:join', { adPublicId });
    }
  }

  // Leave conversation room
  leaveConversation(adPublicId) {
    if (this.socket && this.connected) {
      this.socket.emit('conversation:leave', { adPublicId });
    }
  }

  // Start typing indicator
  startTyping(adPublicId) {
    if (this.socket && this.connected) {
      this.socket.emit('typing:start', { adPublicId });
    }
  }

  // Stop typing indicator
  stopTyping(adPublicId) {
    if (this.socket && this.connected) {
      this.socket.emit('typing:stop', { adPublicId });
    }
  }

  // Mark messages as read
  markAsRead(messageIds) {
    if (this.socket && this.connected) {
      this.socket.emit('messages:read', { messageIds });
    }
  }

  // Get online users
  getOnlineUsers() {
    if (this.socket && this.connected) {
      this.socket.emit('users:getOnline');
    }
  }

  // Listen to events
  on(event, callback) {
    if (!this.socket) {
      console.warn('Cannot add listener: socket not initialized');
      return;
    }

    // Store listener reference for cleanup
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);

    this.socket.on(event, callback);
  }

  // Remove event listener
  off(event, callback) {
    if (this.socket) {
      this.socket.off(event, callback);
    }

    // Remove from listeners map
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  // Remove all listeners for an event
  removeAllListeners(event) {
    if (this.socket) {
      this.socket.off(event);
    }
    if (event) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
  }

  isConnected() {
    return this.connected && this.socket && this.socket.connected;
  }

  getSocket() {
    return this.socket;
  }
}

// Export singleton instance
const chatSocketService = new ChatSocketService();
export default chatSocketService;
